import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { Button, Card, Typography, useToast } from "@codat/orchard-ui";
import { useSendResetPasswordEmail } from "api/password";
import React, { FormEvent, useEffect, useState } from "react";
import formatUrl from "utils/formatUrl";
import { useQuery } from "utils/useQuery";
import { useTitle } from "utils/useTitle";

import AuthPage from "components/AuthPage";
import EmailInput from "components/EmailInput";
import FlexContainer from "components/FlexContainer";

import styles from "./ForgotPassword.module.css";

export const ForgotPassword: React.FC = () => {
  const { sendEvent, sendPageViewEvent } = useAnalytics();

  const [isEmailSent, setIsEmailSent] = useState(false);
  const [email, setEmail] = useState("");
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [displayEmailErrors, setDisplayEmailErrors] = useState(false);
  const { addToast } = useToast();
  const query = useQuery();
  useTitle("Forgot password");

  const [{ loading: resetPasswordLoading }, resetEmail] =
    useSendResetPasswordEmail(email);

  useEffect(() => {
    sendPageViewEvent("Forgot Password");
  }, [sendPageViewEvent]);

  const sendResetEmail = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isEmailValid) {
      setDisplayEmailErrors(true);
      return;
    }
    resetEmail().catch(() => {
      addToast({
        message: `Reset email could not be sent`,
        variant: `error`,
      });
    });
    setIsEmailSent(true);
  };

  if (isEmailSent) {
    return (
      <EventContextProvider name="Forgot Password">
        <AuthPage>
          <Card className={styles.loginCard}>
            <Typography variant="heading_xl" as="h2">
              Forgot password
            </Typography>
            <Typography variant="body_lg" as="h3">
              Check your email for a link to reset your password. If it
              doesn&apos;t appear within a few minutes, check your spam folder.
            </Typography>
            <FlexContainer justifyContent="center">
              <Button
                data-testid="forgot-password-return-to-sign-in"
                to={formatUrl("/signin")}
                className={styles.submitButton}
                label="Return to sign in"
                type="submit"
                onClick={() => sendEvent("Return to Sign In Button - Click")}
              />
            </FlexContainer>
          </Card>
        </AuthPage>
      </EventContextProvider>
    );
  }

  return (
    <AuthPage>
      <Card className={styles.loginCard}>
        <form onSubmit={sendResetEmail}>
          <Typography variant="heading_xl" as="h2">
            Forgot password
          </Typography>
          <Typography variant="body_md" as="h3">
            Enter your account&apos;s verified email and we will send you a
            password reset link.
          </Typography>
          <Typography variant="body_sm" as="h3">
            Please check your spam folder if you don&apos;t see an email from
            us.
          </Typography>
          <EmailInput
            className={styles.emailInputGroup}
            data-testid="forgot-password-email-input"
            setEmail={setEmail}
            value={email}
            isEmailValid={setIsEmailValid}
            displayErrors={displayEmailErrors}
          />
          <Button
            data-testid="forgot-password-email-button"
            label="Send reset email"
            isLoading={resetPasswordLoading}
            type="submit"
            onClick={() => sendEvent("Send Password Reset Button - Click")}
          />
          <Button
            className={styles.backButton}
            data-testid="forgot-password-back-button"
            variant="secondary"
            label="Back to sign in"
            to={formatUrl("/signin", {}, Object.fromEntries(query))}
          />
        </form>
      </Card>
    </AuthPage>
  );
};

export default function ForgotPasswordWithTracking() {
  return (
    <EventContextProvider name="Forgot password">
      <ForgotPassword />
    </EventContextProvider>
  );
}
